import { useEffect } from 'react';

/**
 * Resets the scroll position of the window, document, and all known scrollable containers in the application.
 * Executes immediately and also on the next animation frame to handle DOM updates after state changes.
 *
 * @param {boolean} [smooth=false] - Whether to use smooth scrolling behavior
 */
export const resetScrollToTop = (smooth = false) => {
    const doScroll = () => {
        try {
            window.scrollTo({ top: 0, left: 0, behavior: smooth ? 'smooth' : 'auto' });
        } catch {
            window.scrollTo(0, 0);
        }

        if (document.documentElement && document.documentElement.scrollTop > 0) {
            document.documentElement.scrollTop = 0;
        }
        if (document.body && document.body.scrollTop > 0) {
            document.body.scrollTop = 0;
        }

        // Potential internal scroll containers used across pages, tabs, and editors
        const containerSelectors = [
            '.main-content',
            '.analysis-result-page',
            '.rfp-intake-page',
            '.scanning-container',
            '.go-no-go-results',
            '.go-no-go-tab-content',
            '.gng-tab-pane',
            '.final-card-body',
            '.unified-editor-container',
            '.unified-seamless-editor',
            '.settings-container',
            '.settings-content',
            '.dashboard-container',
            '.dashboard-scroll',
            '.proposals-page',
            '.proposals-page-container',
            '.projects-container',
            '.users-roles-container'
        ];

        containerSelectors.forEach((selector) => {
            const elements = document.querySelectorAll(selector);
            elements.forEach((el) => {
                if (el && typeof el.scrollTop === 'number' && el.scrollTop > 0) {
                    if (smooth && typeof el.scrollTo === 'function') {
                        try {
                            el.scrollTo({ top: 0, behavior: 'smooth' });
                        } catch {
                            el.scrollTop = 0;
                        }
                    } else {
                        el.scrollTop = 0;
                    }
                }
            });
        });
    };

    // Execute immediately
    doScroll();
    // And on next animation frame after React commits DOM updates
    if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(doScroll);
    }
};

/**
 * Custom React hook that automatically resets scroll to top whenever any dependency changes or on mount.
 *
 * @param {Array} [deps=[]] - Array of dependencies to trigger scroll-to-top on change
 * @param {boolean} [smooth=false] - Whether to smooth scroll
 */
export const useScrollToTop = (deps = [], smooth = false) => {
    useEffect(() => {
        resetScrollToTop(smooth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};
